import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

export type ChatThreadKind = 'club' | 'event' | 'pool' | 'outing'
export type ChatMessageStatus = 'sending' | 'sent' | 'failed'

export interface ChatReaction {
  emoji: string
  count: number
  reactedByMe: boolean
}

export interface ChatReplyTo {
  messageId: string
  authorName: string
  body: string
}

export interface ChatMessage {
  id: string
  messageId?: string
  userId: string
  authorName: string
  authorAvatar?: string | null
  body: string
  createdAt: string
  editedAt?: string | null
  deletedAt?: string | null
  isMine?: boolean
  canEdit?: boolean
  canDelete?: boolean
  status?: ChatMessageStatus
  reactions?: ChatReaction[]
  replyTo?: ChatReplyTo | null
  mentions?: string[]
}

export interface ChatThread {
  id: string
  threadId: string
  kind: ChatThreadKind
  entityId?: string
  title: string
  subtitle: string
  preview: string
  updatedAt: string
  path: string
  unread?: number
  muted?: boolean
}

function messagesPath(kind: ChatThreadKind, entityId?: string) {
  if (kind === 'club') return `${API_BASE_URL}/chat/club/main/messages`
  return `${API_BASE_URL}/chat/${kind}/${entityId}/messages`
}

function threadBase(kind: ChatThreadKind, entityId?: string) {
  if (kind === 'club') return `${API_BASE_URL}/chat/club/main`
  return `${API_BASE_URL}/chat/${kind}/${entityId}`
}

class ChatApi {
  private getAccessTokenSilently: (() => Promise<string>) | null = null

  setAuth(getAccessTokenSilently: () => Promise<string>) {
    this.getAccessTokenSilently = getAccessTokenSilently
  }

  private async getHeaders(): Promise<HeadersInit> {
    if (!this.getAccessTokenSilently) {
      throw new Error('Auth not initialized. Call setAuth() first.')
    }
    const token = await this.getAccessTokenSilently()
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }

  private async parseError(response: Response) {
    const err = await response.json().catch(() => ({}))
    const error = new Error(err.error || err.lockedMessage || `Request failed (${response.status})`) as Error & {
      locked?: boolean
      lockedMessage?: string
      status?: number
    }
    error.locked = Boolean(err.locked) || response.status === 403
    error.lockedMessage = err.lockedMessage || err.error
    error.status = response.status
    throw error
  }

  async listThreads(): Promise<ChatThread[]> {
    const headers = await this.getHeaders()
    const response = await fetch(`${API_BASE_URL}/chat/threads`, {
      method: 'GET',
      headers,
      credentials: 'include',
    })
    if (!response.ok) await this.parseError(response)
    const data = await response.json()
    return data.threads || []
  }

  async listMessages(
    kind: ChatThreadKind,
    entityId?: string,
    options?: { limit?: number; before?: string }
  ): Promise<{ messages: ChatMessage[]; thread?: ChatThread }> {
    const headers = await this.getHeaders()
    const params = new URLSearchParams()
    if (options?.limit) params.set('limit', String(options.limit))
    if (options?.before) params.set('before', options.before)
    const qs = params.toString()
    const response = await fetch(`${messagesPath(kind, entityId)}${qs ? `?${qs}` : ''}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    })
    if (!response.ok) await this.parseError(response)
    return response.json()
  }

  async sendMessage(
    kind: ChatThreadKind,
    body: string,
    entityId?: string,
    replyToMessageId?: string
  ): Promise<ChatMessage> {
    const headers = await this.getHeaders()
    const response = await fetch(messagesPath(kind, entityId), {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ body, replyToMessageId }),
    })
    if (!response.ok) await this.parseError(response)
    const data = await response.json()
    return data.message
  }

  async editMessage(
    kind: ChatThreadKind,
    messageId: string,
    body: string,
    entityId?: string
  ): Promise<ChatMessage> {
    const headers = await this.getHeaders()
    const response = await fetch(`${messagesPath(kind, entityId)}/${messageId}`, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: JSON.stringify({ body }),
    })
    if (!response.ok) await this.parseError(response)
    const data = await response.json()
    return data.message
  }

  async deleteMessage(kind: ChatThreadKind, messageId: string, entityId?: string): Promise<ChatMessage> {
    const headers = await this.getHeaders()
    const response = await fetch(`${messagesPath(kind, entityId)}/${messageId}`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
    })
    if (!response.ok) await this.parseError(response)
    const data = await response.json()
    return data.message
  }

  async toggleReaction(
    kind: ChatThreadKind,
    messageId: string,
    emoji: string,
    entityId?: string
  ): Promise<ChatMessage> {
    const headers = await this.getHeaders()
    const response = await fetch(`${messagesPath(kind, entityId)}/${messageId}/reactions`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ emoji }),
    })
    if (!response.ok) await this.parseError(response)
    const data = await response.json()
    return data.message
  }

  async markRead(kind: ChatThreadKind, entityId?: string): Promise<void> {
    const headers = await this.getHeaders()
    const response = await fetch(`${threadBase(kind, entityId)}/read`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: '{}',
    })
    if (!response.ok) await this.parseError(response)
  }

  async setMute(kind: ChatThreadKind, muted: boolean, entityId?: string): Promise<{ muted: boolean }> {
    const headers = await this.getHeaders()
    const response = await fetch(`${threadBase(kind, entityId)}/mute`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: JSON.stringify({ muted }),
    })
    if (!response.ok) await this.parseError(response)
    const data = await response.json()
    return { muted: Boolean(data.muted) }
  }
}

const chatApi = new ChatApi()

export function useChatApi() {
  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    chatApi.setAuth(getAccessTokenSilently)
  }, [getAccessTokenSilently])

  return chatApi
}

export default chatApi
